import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Camera, Wrench, CalendarCheck, Truck } from 'lucide-react';
import { assistAPI } from '../services/api';
import { i18n, useT, DICTS } from '../services/i18n';
import { browserNotif } from '../services/browserNotif';

const timeAgo = (iso, t) => {
  const d = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (d < 60) return t('notif.agoMin', { n: Math.max(1, d) });
  if (d < 1440) return t('notif.agoH', { n: Math.floor(d / 60) });
  return t('notif.agoD', { n: Math.floor(d / 1440) });
};

const TITLE_KEYS = { 'Référence confirmée pour votre pièce': 'notif.refConfirmed', 'Votre demande de vérification photo': 'notif.photoRequest', 'Rendez-vous de montage déplacé': 'notif.fittingMoved', 'Rendez-vous de montage confirmé': 'notif.fittingConfirmed', 'Montage de vos pneus demain': 'notif.fittingTomorrow' };
// Legacy notifications (no tkey) store French titles; known titles are re-labelled in the UI language
const legacyTitle = (title, t) => {
  if (TITLE_KEYS[title]) return t(TITLE_KEYS[title]);
  const m = /^Entretien à prévoir : (.+) \((.+)\)$/.exec(title || '');
  return m ? t('notif.maintenance', { sub: m[1], vehicle: m[2] }) : title;
};

// Structured notifications (tkey + vars) are rendered in the current UI language
const displayVars = (vars, lang) => {
  const out = {};
  Object.entries(vars || {}).forEach(([k, v]) => {
    if (k.endsWith('_iso')) out[k.slice(0, -4)] = i18n.formatDateTime(v);
    else if (k === 'amount_eur') out.amount = new Intl.NumberFormat(i18n.locale(), { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);
    else if (k !== 'sub_i18n') out[k] = v ?? '';
  });
  if (vars?.sub_i18n?.[lang]) out.sub = vars.sub_i18n[lang];
  return out;
};
export const notifText = (n, t, lang) => {
  if (n.tkey && DICTS.fr[`notif.k.${n.tkey}.title`]) {
    const vars = displayVars(n.vars, lang);
    return { title: t(`notif.k.${n.tkey}.title`, vars), body: t(`notif.k.${n.tkey}.body`, vars) };
  }
  return { title: legacyTitle(n.title, t), body: n.body };
};

export const NotificationsBell = ({ user }) => {
  const { t, lang } = useT();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!user) return;
    const load = () => assistAPI.notifications({ limit: 20 }).then((d) => {
      setData((prev) => {
        // Native browser alerts for new fitting notifications (opt-in, only when we already knew the previous list)
        if (prev.loaded) {
          const known = new Set(prev.items.map((n) => n.id));
          d.items.filter((n) => !known.has(n.id) && !n.read && ['fitting', 'delivery'].includes(n.kind)).forEach((n) => { const { title, body } = notifText(n, i18n.t, i18n.get()); browserNotif.show(title, body, n.url); });
        }
        return { ...d, loaded: true };
      });
    }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  if (!user) return null;
  const toggle = async () => {
    setOpen((o) => !o);
    if (!open && data.unread) { await assistAPI.markRead().catch(() => {}); setData((d) => ({ ...d, unread: 0, items: d.items.map((n) => ({ ...n, read: true })) })); }
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={toggle} className="relative p-1 text-white hover:text-orange-400" title={t('notif.title')} data-testid="notifications-bell">
        <Bell size={22} />
        {data.unread > 0 && <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="notifications-unread">{data.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white text-gray-900 rounded-xl shadow-2xl border z-50 overflow-hidden" data-testid="notifications-panel">
          <div className="px-4 py-3 border-b font-semibold text-sm">{t('notif.title')}</div>
          <div className="max-h-96 overflow-y-auto divide-y">
            {data.items.length === 0 && <p className="p-4 text-sm text-gray-500" data-testid="notifications-empty">{t('notif.empty')}</p>}
            {data.items.map((n) => {
              const { title, body } = notifText(n, t, lang);
              return (
              <Link key={n.id} to={n.url || '/mon-compte'} onClick={() => setOpen(false)} className="flex gap-3 p-3 hover:bg-orange-50" data-testid={`notification-${n.id}`}>
                <span className={`mt-0.5 shrink-0 rounded-full p-1.5 ${n.kind === 'maintenance' ? 'bg-blue-100 text-blue-700' : n.kind === 'fitting' ? 'bg-emerald-100 text-emerald-700' : n.kind === 'delivery' ? 'bg-sky-100 text-sky-700' : 'bg-orange-100 text-orange-700'}`} data-testid={n.kind === 'fitting' ? `notification-fitting-${n.event}` : n.kind === 'delivery' ? 'notification-delivery' : undefined}>{n.kind === 'maintenance' ? <Wrench size={14} /> : n.kind === 'fitting' ? <CalendarCheck size={14} /> : n.kind === 'delivery' ? <Truck size={14} /> : <Camera size={14} />}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900" data-testid={`notification-title-${n.id}`}>{title}</span>
                  <span className="block text-xs text-gray-600 line-clamp-2" data-testid={`notification-body-${n.id}`}>{body}</span>
                  <span className="block text-[11px] text-gray-400 mt-0.5">{timeAgo(n.created_at, t)}</span>
                </span>
              </Link>
              );
            })}
          </div>
          <div className="flex border-t text-xs">
            <Link to="/mon-compte/notifications" onClick={() => setOpen(false)} className="flex-1 text-center text-orange-600 font-medium py-2 hover:bg-gray-50 border-r" data-testid="notifications-see-all">{t('notif.seeAll')}</Link>
            <Link to="/mon-compte/entretien" onClick={() => setOpen(false)} className="flex-1 text-center text-gray-600 py-2 hover:bg-gray-50" data-testid="notifications-maintenance-link">{t('notif.maintenanceLink')}</Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
